import { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';
import { getAllTechnologies } from '../../../../api/sales';
import { getAllFaculty } from '../../../../api/training';
import { formatDateCST, formatTimeCST } from '../../../../utlis';

// Status options
const statusOptions = [
  { value: 'ENROLLMENT', text: 'Enrollment' },
  { value: 'CLOSED', text: 'Closed' },
  { value: 'COMPLETED', text: 'Completed' },
];

const ClassesModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  isSubmitting,
}) => {
  // State for technologies and faculty fetched from API
  const [technologies, setTechnologies] = useState([]);
  const [isLoadingTechnologies, setIsLoadingTechnologies] = useState(false);
  const [facultyList, setFacultyList] = useState([]);
  const [isLoadingFaculty, setIsLoadingFaculty] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      description: '',
      faculty_id: '',
      technology_id: '',
      start_date: '',
      end_date: '',
      min_enrollment: '',
      max_enrollment: '',
      status: 'ENROLLMENT',
    },
  });

  // Watch all form fields for changes
  const watchedFields = watch();

  // Check if form has any changes compared to initial values
  const hasChanges = () => {
    if (!initialValues) return !isEditMode; // For new class, always enable save

    const fieldsToCompare = ['description', 'faculty_id', 'technology_id', 'start_date', 'end_date', 'min_enrollment', 'max_enrollment', 'status'];

    return fieldsToCompare.some((field) => {
      const currentValue = String(watchedFields[field] || '').trim();
      const initialValue = String(initialValues[field] || '').trim();
      return currentValue !== initialValue;
    });
  };

  // Fetch technologies from API when modal opens
  useEffect(() => {
    const fetchTechnologies = async () => {
      setIsLoadingTechnologies(true);
      try {
        const response = await getAllTechnologies();
        const techData = response?.data?.data?.records || response?.data?.records || response?.data || [];
        setTechnologies(Array.isArray(techData) ? techData : []);
      } catch (error) {
        console.error('Error fetching technologies:', error);
        setTechnologies([]);
      } finally {
        setIsLoadingTechnologies(false);
      }
    };

    if (isOpen) {
      fetchTechnologies();
    }
  }, [isOpen]);

  // Fetch faculty from API when modal opens
  useEffect(() => {
    const fetchFaculty = async () => {
      setIsLoadingFaculty(true);
      try {
        const response = await getAllFaculty(1, 100);
        const facultyData = response?.data?.data?.records || response?.data?.records || response?.data || [];
        setFacultyList(Array.isArray(facultyData) ? facultyData : []);
      } catch (error) {
        console.error('Error fetching faculty:', error);
        setFacultyList([]);
      } finally {
        setIsLoadingFaculty(false);
      }
    };

    if (isOpen) {
      fetchFaculty();
    }
  }, [isOpen]);

  // Reset form when modal opens or defaultValues change
  useEffect(() => {
    if (isOpen && defaultValues) {
      reset(defaultValues);

      // Store initial values for change detection
      setInitialValues({
        description: defaultValues.description || '',
        faculty_id: defaultValues.faculty_id || '',
        technology_id: defaultValues.technology_id || '',
        start_date: defaultValues.start_date || '',
        end_date: defaultValues.end_date || '',
        min_enrollment: defaultValues.min_enrollment || '',
        max_enrollment: defaultValues.max_enrollment || '',
        status: defaultValues.status || 'ENROLLMENT',
      });
    }
  }, [isOpen, defaultValues, reset]);

  const handleClose = () => {
    reset({
      description: '',
      faculty_id: '',
      technology_id: '',
      start_date: '',
      end_date: '',
      min_enrollment: '',
      max_enrollment: '',
      status: 'ENROLLMENT',
    });
    setInitialValues(null);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[900px] max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Modal Title and Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              {isEditMode ? 'Edit Class' : 'Add New Class'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form
            className="flex flex-col gap-4 md:gap-6 text-text-hint"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Row 1: Class Name/Description, Faculty, Technology */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="description" className="text-text-hint mb-1">
                  Class Name <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('description', {
                    required: 'Class Name is required',
                    maxLength: { value: 100, message: 'Max 100 characters' },
                  })}
                  type="text"
                  placeholder="Enter class name"
                  id="description"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.description && (
                  <span className="text-red-500 text-xs">{errors.description.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="faculty_id" className="text-text-hint mb-1">
                  Faculty
                </label>
                <select
                  {...register('faculty_id')}
                  id="faculty_id"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                  disabled={isLoadingFaculty}
                >
                  <option value="">
                    {isLoadingFaculty ? 'Loading...' : '--Select Faculty--'}
                  </option>
                  {facultyList.map((faculty) => (
                    <option key={faculty.faculty_id} value={faculty.faculty_id}>
                      {`${faculty.first_name || ''} ${faculty.last_name || ''}`.trim() || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="technology_id" className="text-text-hint mb-1">
                  Technology
                </label>
                <select
                  {...register('technology_id')}
                  id="technology_id"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                  disabled={isLoadingTechnologies}
                >
                  <option value="">
                    {isLoadingTechnologies ? 'Loading...' : '--Select Technology--'}
                  </option>
                  {technologies.map((tech) => (
                    <option key={tech.technology_id} value={tech.technology_id}>
                      {tech.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Start Date, End Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="start_date" className="text-text-hint mb-1">
                  Start Date <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('start_date', { required: 'Start Date is required' })}
                  type="date"
                  id="start_date"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.start_date && (
                  <span className="text-red-500 text-xs">{errors.start_date.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="end_date" className="text-text-hint mb-1">
                  End Date <span className="text-danger-500">*</span>
                </label>
                <input
                  {...register('end_date', { required: 'End Date is required' })}
                  type="date"
                  id="end_date"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.end_date && (
                  <span className="text-red-500 text-xs">{errors.end_date.message}</span>
                )}
              </div>
            </div>

            {/* Row 3: Min Enrollment, Max Enrollment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
              <div className="flex flex-col space-y-1">
                <label htmlFor="min_enrollment" className="text-text-hint mb-1">
                  Min Enrollment
                </label>
                <input
                  {...register('min_enrollment', {
                    min: { value: 0, message: 'Must be 0 or greater' },
                  })}
                  type="number"
                  placeholder="Minimum enrollment"
                  id="min_enrollment"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.min_enrollment && (
                  <span className="text-red-500 text-xs">{errors.min_enrollment.message}</span>
                )}
              </div>

              <div className="flex flex-col space-y-1">
                <label htmlFor="max_enrollment" className="text-text-hint mb-1">
                  Max Enrollment
                </label>
                <input
                  {...register('max_enrollment', {
                    min: { value: 0, message: 'Must be 0 or greater' },
                  })}
                  type="number"
                  placeholder="Maximum enrollment"
                  id="max_enrollment"
                  className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
                />
                {errors.max_enrollment && (
                  <span className="text-red-500 text-xs">{errors.max_enrollment.message}</span>
                )}
              </div>
            </div>

            {/* Row 4: Status */}
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint mb-1">
                Class Status <span className="text-danger-500">*</span>
              </label>
              <ul className="grid grid-cols-3 w-80">
                {statusOptions.map((option, index) => (
                  <li key={option.value} className="relative">
                    <input
                      {...register('status', { required: 'Status is required' })}
                      className="sr-only peer"
                      type="radio"
                      value={option.value}
                      id={`status-${option.value}`}
                    />
                    <label
                      className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 text-sm ${
                        index === 0
                          ? 'rounded-tl-full rounded-bl-full'
                          : index === statusOptions.length - 1
                          ? 'rounded-tr-full rounded-br-full'
                          : ''
                      }`}
                      htmlFor={`status-${option.value}`}
                    >
                      {option.text}
                    </label>
                  </li>
                ))}
              </ul>
              {errors.status && (
                <span className="text-red-500 text-xs">{errors.status.message}</span>
              )}
            </div>

            {/* Display-only fields for Edit Mode */}
            {isEditMode && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Created By</label>
                  <p className="text-sm text-gray-600">{defaultValues?.created_by || 'N/A'}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Date Created</label>
                  <p className="text-sm text-gray-600">
                    {defaultValues?.created ? formatDateCST(defaultValues.created) : 'N/A'}
                  </p>
                  {defaultValues?.created && (
                    <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.created)}</p>
                  )}
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Updated By</label>
                  <p className="text-sm text-gray-600">{defaultValues?.updated_by || 'N/A'}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-text-hint text-xs">Last Updated</label>
                  <p className="text-sm text-gray-600">
                    {defaultValues?.updated ? formatDateCST(defaultValues.updated) : 'N/A'}
                  </p>
                  {defaultValues?.updated && (
                    <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.updated)}</p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                <button
                  type="submit"
                  disabled={isSubmitting || !hasChanges()}
                  className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Saving...'
                    : isEditMode
                    ? 'Update'
                    : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default ClassesModal;
